import { ApiResponse } from '@/services';
import request from '@/utils/request';

export interface IAssetProps {
  goodsId?: number;
  type: number; //type 1-文件夹，2-图片，3-视频，4-字体,5-音频,6- 音色音频 ，7- zip 文件,8-文本
  title?: string;
  desc?: string;
  fileUrl?: string; // 文件地址或者文本内容
  duration?: number; // 时长
  cover?: string;
  customType?: string;
}

export const saveAsset = async <T>(data: IAssetProps): Promise<ApiResponse<T>> => {
  const res = await request('/api/rest/dlt/asset/add', {
    method: 'POST',
    data,
  });
  return res;
};

export const saveBatchAsset = async <T>(data: IAssetProps[]): Promise<ApiResponse<T>> => {
  const res = await request('/api/rest/dlt/asset/batch/add', {
    method: 'POST',
    data: {
      assetList: data,
    },
  });
  return res;
};

export const updateAsset = async <T>(data: {
  id: number;
  title: string;
  text: string;
}): Promise<ApiResponse<T>> => {
  const res = await request('/api/rest/dlt/asset/update', {
    method: 'POST',
    data,
  });
  return res;
};

export interface CreateAvatarParams {
  videoUrl: string;
  title: string;
  gender: number;
  previewUrl: string;
  voiceClone?: boolean;
  voiceUrl?: string;
  // 默认是5，克隆音色打开时传6
  voiceCloneType?: number;
  voicePreviewText?: string;
}
// 创建数字人
export const createAvatar = async <T>(
  data: CreateAvatarParams,
): Promise<ApiResponse<T>> => {
  const res = await request('/api/rest/dlt/cloud/avatar/train', {
    method: 'POST',
    data,
  });
  return res;
};

export interface CreateAvatarByVideoParams {
  videoUrl: string;
  title: string;
  gender: number;
  previewUrl: string;
  voiceClone?: boolean;
  voiceUrl?: string;
  // 默认是5，克隆音色打开时传6
  voiceCloneType?: number;
  voicePreviewText?: string;
}
// 创建数字人
export const createAvatarByVideo = async <T>(
  data: CreateAvatarByVideoParams,
): Promise<ApiResponse<T>> => {
  const res = await request('/api/rest/dlt/cloud/avatar/train', {
    method: 'POST',
    data,
  });
  return res;
};
export interface CreateAvatarByImageParams {
  imgUrl: string;
  avatarName: string;
  gender: number;
  prompt: string;
}
export const createAvatarByImage = async <T>(
  data: CreateAvatarByImageParams,
): Promise<ApiResponse<T>> => {
  const res = await request('/api/rest/dlt/agent/img2Avatar', {
    method: 'POST',
    data,
  });
  return res;
};

export interface CreateVoiceParams {
  voiceUrl: string;
  title: string;
  gender: 1 | 2;
  voiceCloneType: 5 | 6;
  voicePreviewText: string;
  needTemplate: boolean;
  needSaveAsset: boolean;
}
// 创建音色
export const createVoice = async <T>(
  data: CreateVoiceParams,
): Promise<ApiResponse<T>> => {
  const res = await request('/api/rest/dlt/cloud/voice/clone', {
    method: 'POST',
    data,
  });
  return res;
};
export const createNewVoice = async <T>(
  data: CreateVoiceParams,
): Promise<ApiResponse<T>> => {
  const res = await request('/api/rest/dlt/f/cloud/voice/clone', {
    method: 'POST',
    data,
  });
  return res;
};

// 获取商业化所有下发配置
export const getLimitConfig = async () => {
  const res = await request('/api/rest/dlt/commerce/vip/function', {
    method: 'POST',
  });
  return res;
};

// 文本内容审核
export const textComplianceReview = async (text: string) => {
  const res = await request('/api/rest/dlt/review/sync/text', {
    method: 'POST',
    data: {
      text,
    },
  });
  return res;
};

/**
 * 查询分销码
 *
 * @param {Object} params - 查询参数
 * @param { 1 | 2} params.type - 1查询自己的分销码 2查询绑定的分销码
 */
export const getDistributionCode = async <T>(type: 1 | 2): Promise<ApiResponse<T>> => {
  const res = await request('/api/rest/dlt/user/right/distribution/query', {
    method: 'POST',
    data: {
      type,
    },
  });
  return res;
};

export enum RewardType {
  MONEY = 1,
  POINT = 2,
}
/**
 * 分销记录单条数据类型
 */
export interface DistributionRecord {
  /** 被邀请用户ID（可选） */
  invitedUserId?: number;
  invitedUserName?: string;
  /** 分销码（可选） */
  distributionCode?: string;
  /** 分销码拥有者用户ID（必须） */
  userId: string;
  /** 订单金额（可选） */
  orderAmount?: number;
  /** 收益金额（可选） */
  proceeds?: number;
  /** 订单币种（可选） */
  orderCurrency?: string;
  /** 记录状态（0-支付中，1-已完成，2-无效记录）（可选） */
  recordState?: '0' | '1' | '2';
  /** 创建时间（可选） */
  createTime: number;
  /** 更新时间（可选） */
  updateTime: number;
  rewardType: RewardType; // 奖励类型（1-现金奖励，2-积分奖励）
}

/**
 * 查询分销记录
 *
 * @param {Object} params - 查询参数
 * @param {1 | 2} params.rewardType - 奖励类型（1-现金奖励，2-积分奖励）
 * @param {0 | 1 | 2} params.recordState - 记录状态（0-支付中，1-已完成，2-无效记录）
 * @param {number} params.pageNum - 当前页码
 * @param {number} params.pageSize - 每页条数
 *
 * @returns {Promise<any>} 分销记录查询结果
 */

export const getDistributionList = async <T>({
  recordState,
  rewardType,
  pageNum,
  pageSize,
}: {
  recordState: 0 | 1 | 2;
  rewardType: RewardType;
  pageNum: number;
  pageSize: number;
}): Promise<ApiResponse<T>> => {
  const res = await request('/api/rest/dlt/user/right/proceedsRecord/query', {
    method: 'POST',
    data: {
      recordState,
      rewardType,
      pageNum,
      pageSize,
    },
  });
  return res;
};

/**
 * 解析URL返回数据的类型定义
 */
export interface ParseUrlResponse {
  /** 任务 id */
  businessId: string;
}

/**
 * 解析URL
 *
 * @param {string} originUrl - 待解析的原始URL
 * @returns {Promise<ApiResponse<ParseUrlResponse>>} 解析结果
 */
export const parseUrl = async <T>(originUrl: string): Promise<ApiResponse<T>> => {
  // MOCK数据，用于测试
  const isMock = false; // 设置为true启用mock，false则使用实际API

  if (isMock) {
    // 模拟网络延迟
    await new Promise((resolve) => setTimeout(resolve, 1800));

    // 更宽松的平台检测
    const trimmedUrl = originUrl.trim().toLowerCase();

    // 只要URL中包含这些关键词，就视为相应平台的链接
    const isYouTube =
      trimmedUrl.includes('youtube') ||
      trimmedUrl.includes('youtu.be') ||
      trimmedUrl.includes('yt.') ||
      trimmedUrl.includes('ytimg');

    const isTikTok =
      trimmedUrl.includes('tiktok') ||
      trimmedUrl.includes('douyin') ||
      trimmedUrl.includes('tiktokcdn') ||
      trimmedUrl.includes('tt.');

    // 模拟失败情况 - 不支持的平台
    if (!isYouTube && !isTikTok) {
      return {
        success: false,
        code: 400,
        message: '不支持的平台，当前仅支持YouTube和TikTok',
        data: null,
      } as unknown as ApiResponse<T>;
    }

    // 模拟失败情况 - 极端情况的无效URL (只判断极短的输入)
    if (originUrl.trim().length < 5) {
      return {
        success: false,
        code: 400,
        message: '无效的URL地址',
        data: null,
      } as unknown as ApiResponse<T>;
    }

    // 模拟成功情况 - YouTube
    if (isYouTube) {
      return {
        success: true,
        code: 200,
        message: '解析成功',
        data: {
          businessId: `yt-${Date.now()}`,
        },
      } as unknown as ApiResponse<T>;
    }

    // 模拟成功情况 - TikTok
    if (isTikTok) {
      return {
        success: true,
        code: 200,
        message: '解析成功',
        data: {
          businessId: `tt-${Date.now()}`,
        },
      } as unknown as ApiResponse<T>;
    }
  }

  // 实际API调用
  const res = await request('/api/rest/dlt/cloud/url/parse', {
    method: 'POST',
    data: {
      originUrl,
    },
  });
  return res;
};

/**
 * 解析URL结果查询返回的数据类型
 */
export interface ParseUrlResultResponse {
  /** 视频下载链接 */
  url: string;
  /** 任务状态: 1-进行中, 2-成功, 3-失败 */
  state: 1 | 2 | 3;
  /** 状态消息 */
  message?: string;
}

/**
 * 获取URL解析的结果
 *
 * @param {string} businessId - 解析任务的业务ID
 * @returns {Promise<ApiResponse<ParseUrlResultResponse>>} 解析结果
 */
export const getParseUrlResult = async <T>(
  businessId: string,
): Promise<ApiResponse<T>> => {
  // MOCK数据，用于测试
  const isMock = false; // 设置为true启用mock，false则使用实际API

  if (isMock) {
    // 模拟网络延迟
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // 检查businessId是否有效
    if (!businessId || typeof businessId !== 'string') {
      return {
        success: false,
        code: 400,
        message: '无效的任务ID',
        data: null,
      } as unknown as ApiResponse<T>;
    }

    // 模拟任务处理中状态 (基于随机逻辑可以注释掉这部分)
    // const isProcessing = Math.random() > 0.7;
    // if (isProcessing) {
    //   return {
    //     success: true,
    //     code: 200,
    //     message: '任务处理中',
    //     data: {
    //       url: '',
    //       state: 1,
    //       message: '视频正在解析中，请稍后再试'
    //     },
    //   } as unknown as ApiResponse<T>;
    // }

    // 根据businessId前缀判断平台
    const isPlatformYouTube = businessId.startsWith('yt-');
    const isPlatformTikTok = businessId.startsWith('tt-');

    // 模拟成功情况
    if (isPlatformYouTube) {
      return {
        success: true,
        code: 200,
        message: '解析成功',
        data: {
          url: 'https://example-cdn.com/youtube-video-download.mp4',
          state: 2,
          message: '解析成功',
        },
      } as unknown as ApiResponse<T>;
    }

    if (isPlatformTikTok) {
      return {
        success: true,
        code: 200,
        message: '解析成功',
        data: {
          url: 'https://example-cdn.com/tiktok-video-download.mp4',
          state: 2,
          message: '解析成功',
        },
      } as unknown as ApiResponse<T>;
    }

    // 未知平台或其他错误
    return {
      success: false,
      code: 400,
      message: '无法识别的任务ID',
      data: null,
    } as unknown as ApiResponse<T>;
  }

  // 实际API调用
  const res = await request('/api/rest/dlt/cloud/url/parse/result', {
    method: 'POST',
    data: {
      businessId,
    },
  });
  return res;
};
